import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  User, Cpu, Power, Wallet, Flame, Cylinder, Gauge, Zap, Clock, ShieldCheck,
  CalendarDays, TrendingUp,
} from "lucide-react";
import axios from "axios";

// Gasify brand tones
const NAVY = "#0a1628";
const FLAME = "#f97316";

// Public "app home" QR landing page — no auth required.
export default function HouseholdPublicCard() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/households/${id}/public`;
    axios.get(url).then((r) => setD(r.data)).catch(() => setErr("Household not found"));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
        <div className="max-w-sm w-full bg-white border border-slate-200 rounded-sm p-6 text-center" data-testid="public-card-error">
          <div className="text-flame-600 font-semibold">Household not found</div>
          <div className="text-xs text-slate-500 mt-1">Please verify the QR code or contact Gasify support.</div>
        </div>
      </div>
    );
  }
  if (!d) {
    return <div className="min-h-screen grid place-items-center text-sm text-slate-400" data-testid="public-card-loading">Loading…</div>;
  }

  const currency = d.currency || "UGX";
  const fmtUGX = (n) => `${currency} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const fmtKg  = (n) => `${Number(n || 0).toFixed(2)} kg`;
  const fmtM3  = (n) => `${Number(n || 0).toFixed(3)} m³`;
  const pct = d.remaining_pct ?? 0;
  const cylTone = pct < 20 ? "bg-flame-500" : pct < 50 ? "bg-amber-500" : "bg-emerald-500";
  const valveOpen = d.valve_state === "OPEN";
  const balance = d.credit || {};
  const usage = d.consumption || {};

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }} data-testid="public-card-page">
      {/* Hero header — Gasify navy with orange accent */}
      <header
        className="text-white relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #10233d 100%)` }}
      >
        {/* Decorative flame */}
        <div className="absolute -right-6 -top-4 opacity-[0.08]">
          <Flame size={240} style={{ color: FLAME }} strokeWidth={1} />
        </div>
        <div className="max-w-md mx-auto px-4 sm:px-5 pt-8 sm:pt-10 pb-14 sm:pb-16 relative">
          <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tight break-words" data-testid="public-hh-name">
            {d.household_name}
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-3 sm:px-5 -mt-10 space-y-3 sm:space-y-4 pb-8">
        {/* 1. Personal data — meter number only */}
        <SectionCard testid="section-personal" title="Personal data" icon={User}>
          <Row icon={Cpu} label="Meter number" value={d.meter_serial || "— unassigned —"} mono />
        </SectionCard>

        {/* 2. Meter valve status */}
        <SectionCard testid="section-valve" title="Meter valve" icon={Power}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-10 w-10 sm:h-11 sm:w-11 rounded-full grid place-items-center shrink-0 ${valveOpen ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                <Power size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Current state</div>
                <div className={`font-heading text-lg font-bold ${valveOpen ? "text-emerald-700" : "text-slate-700"}`} data-testid="public-valve-state">
                  {d.valve_state || "—"}
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Last updated</div>
              <div className="text-xs text-slate-700 tabular-nums" data-testid="public-last-uplink">
                {d.last_report_at ? new Date(d.last_report_at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
              </div>
            </div>
          </div>
        </SectionCard>

        {/* 3. Credit status */}
        <SectionCard testid="section-credit" title="Credit status" icon={Wallet} filled>
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-heading font-bold text-2xl sm:text-3xl tabular-nums text-white break-words" data-testid="public-balance-ugx">
              {fmtUGX(balance.balance_ugx)}
            </div>
          </div>
          <div className="text-xs text-white/85 tabular-nums mt-1 break-words" data-testid="public-balance-kg">
            ≈ {fmtKg(balance.balance_kg)} · {fmtM3(balance.balance_m3)} of gas
          </div>
        </SectionCard>

        {/* 4. Consumption analytics */}
        <SectionCard testid="section-consumption" title="Consumption" icon={Zap}>
          <div className="grid grid-cols-2 gap-3">
            <Stat
              icon={Clock}
              label="Today"
              primary={fmtM3(usage.today_m3)}
              sub={fmtUGX(usage.today_ugx)}
              testId="usage-today"
            />
            <Stat
              icon={CalendarDays}
              label="This month"
              primary={fmtM3(usage.month_m3)}
              sub={fmtUGX(usage.month_ugx)}
              testId="usage-month"
            />
            <Stat
              icon={Gauge}
              label="Cumulative use"
              primary={fmtM3(usage.cumulative_m3)}
              sub={fmtUGX(usage.cumulative_ugx)}
              extra={fmtKg(usage.cumulative_kg)}
              testId="usage-total"
            />
            <Stat
              icon={TrendingUp}
              label="Avg / day (7-day)"
              primary={fmtM3(usage.avg_daily_m3)}
              sub={fmtUGX(usage.avg_daily_ugx)}
              extra="rolling window"
              testId="usage-avg"
            />
          </div>
        </SectionCard>
      </main>
    </div>
  );
}

// ---------- helpers ---------------------------------------------------------
function SectionCard({ title, icon: Icon, children, testid, filled }) {
  const outlineStyle = filled
    ? { background: "#1E579E", borderColor: "#174682" }   // Gasify brand-600 fill (logo blue), darker border
    : { borderColor: "#B0C8E6" };                          // Gasify brand-200 outline
  return (
    <section
      className="border-2 rounded-xl shadow-sm"
      style={outlineStyle}
      data-testid={testid}
    >
      <div className={`px-3 sm:px-4 pt-3 pb-2 flex items-center gap-2 border-b ${filled ? "border-white/20" : "border-slate-100"}`}>
        <div className={`h-6 w-6 rounded-md grid place-items-center shrink-0 ${filled ? "bg-white/15 text-white" : "bg-slate-100 text-slate-900"}`}>
          <Icon size={13} />
        </div>
        <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${filled ? "text-white/80" : "text-slate-600"}`}>{title}</div>
      </div>
      <div className="p-3 sm:p-4">{children}</div>
    </section>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="h-8 w-8 rounded-md bg-slate-100 grid place-items-center text-slate-900 shrink-0">
        <Icon size={13} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className={`text-sm font-semibold text-slate-900 truncate ${mono ? "font-mono tabular-nums" : ""}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function Divider() { return <div className="h-px bg-slate-100" />; }

function Stat({ icon: Icon, label, primary, sub, extra, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/50" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-white border border-slate-200 grid place-items-center text-slate-900 shrink-0">
          <Icon size={13} />
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold leading-tight">{label}</div>
      </div>
      <div className="mt-2 font-heading font-bold text-slate-900 tabular-nums text-lg leading-none">{primary}</div>
      <div className="text-[11px] text-slate-600 tabular-nums mt-1">{sub}</div>
      {extra && <div className="text-[10px] text-slate-400 mt-0.5">{extra}</div>}
    </div>
  );
}
